import { useEffect, useState } from "react"

/**
 * Duration in milliseconds
 */
const secondInMs = 1000
const minuteInMs = secondInMs * 60
const hourInMs = minuteInMs * 60
const dayInMs = hourInMs * 24
const weekInMs = dayInMs * 7

/**
 * The Javascript Date constructor never throws an error, even when provided weird parameters.
 * However, they can be internally invalid.
 */
export function isValidDate(date: Date) {
  return !isNaN(date.getTime())
}

/**
 * Create a valid Date from an ISO 8601 string or returns undefined.
 */
export function dateFromISOString(str: string): Date | undefined {
  const date = new Date(str)
  return isValidDate(date) ? date : undefined
}

/**
 * Returns a human readable string of how long ago the specified date is from now.
 * < 60s -> "Just now"
 * < 1h -> "X minutes ago"
 * < 1d -> "X hours ago"
 * > 1d -> formatted date
 * @param date
 */
export function getDateDiffString(date: Date) {
  const days = getDaysFrom(date)
  const minutes = getMinutesFrom(date)

  if (days > 0)
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })

  if (minutes > 0) return getRelativeTimeString({ date })

  return "Just now"
}

interface PrettyPrintDateOptions {
  locale?: string
  year?: boolean
  time?: boolean
}

/**
 * Should mainly be used for debugging or very rare formatting use cases.
 * Example output:  Apr 18, 2025, 03:36 PM
 */
export function prettyPrintDate(
  date: Date,
  options: PrettyPrintDateOptions = {},
) {
  const { locale = "en-US", year = true, time = true } = options

  const formatter = new Intl.DateTimeFormat(locale, {
    year: year ? "numeric" : undefined,
    month: "short",
    day: "numeric",
    ...(time ? { hour: "2-digit", minute: "2-digit" } : {}),
  })

  return formatter.format(date)
}

/**
 * Example output:  "2024-12-22"
 */
export function getSimpleISOString(date: Date) {
  const YYYY = date.getUTCFullYear().toString().padStart(4, "0")
  const MM = (date.getUTCMonth() + 1).toString().padStart(2, "0")
  const DD = date.getUTCDate().toString().padStart(2, "0")
  return `${YYYY}-${MM}-${DD}`
}

interface GetRelativeTimeStringArgs {
  date: Date
  lang?: string
  style?: "short" | "long" | "narrow"
}

/**
 * Convert a date to a relative time string, such as
 * "a minute ago", "in 2 hours", "yesterday", "3 months ago", etc.
 * using Intl.RelativeTimeFormat
 *
 * based on Steve Sewells getRelativeTimeString function [Fast and Light Relative Time Strings in JS](https://www.builder.io/blog/relative-time)
 */
export function getRelativeTimeString(args: GetRelativeTimeStringArgs) {
  const { date, lang = "en-US", style = "short" } = args

  // Allow dates or times to be passed
  const timeMs = date.getTime()

  // Get the amount of seconds between the given date and now
  const deltaSeconds = Math.round((timeMs - Date.now()) / 1000)

  // Get numberOfDaysInMonth for the given date to handle months with different number of days
  const numberOfDaysInMonth = getNumberOfDaysInMonth(date)

  // Array reprsenting one minute, hour, day, week, month, etc in seconds
  const cutoffs = [
    60,
    3600,
    86400,
    86400 * 7,
    86400 * numberOfDaysInMonth,
    86400 * 365,
    Infinity,
  ] as const

  // Array equivalent to the above but in the string representation of the units
  const units: Intl.RelativeTimeFormatUnit[] = [
    "second",
    "minute",
    "hour",
    "day",
    "week",
    "month",
    "year",
  ]

  // Grab the ideal cutoff unit
  const unitIndex = cutoffs.findIndex(
    (cutoff) => cutoff > Math.abs(deltaSeconds),
  )

  // Get the divisor to divide from the seconds. E.g. if our unit is "day" our divisor
  // is one day in seconds, so we can divide our seconds by this to get the # of days
  const divisor = unitIndex ? (cutoffs[unitIndex - 1] ?? 1) : 1

  // Intl.RelativeTimeFormat do its magic
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto", style })
  return rtf.format(
    Math.floor(deltaSeconds / divisor),
    units[unitIndex] ?? "second",
  )
}

export function getNumberOfDaysInMonth(date: Date) {
  return new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0, // Last day of the month
  ).getDate()
}

export function getMinutesFrom(date: Date) {
  const now = new Date()
  const diff = now.getTime() - date.getTime()

  return Math.floor(diff / (60 * 1000))
}

export function getHoursFrom(date: Date) {
  const minutes = getMinutesFrom(date)

  return Math.floor(minutes / 60)
}

export function getDaysFrom(date: Date) {
  const hours = getHoursFrom(date)

  return Math.floor(hours / 24)
}

export function getMonthsFrom(date: Date) {
  const currentDate = new Date()

  const yearDifference = currentDate.getFullYear() - date.getFullYear()
  const monthDifference = currentDate.getMonth() - date.getMonth()

  return yearDifference * 12 + monthDifference
}

export function addDays(date: Date, numberOfDays: number): Date {
  const newDate = new Date(date)
  newDate.setUTCDate(date.getUTCDate() + numberOfDays)
  return newDate
}

export function addWeeks(date: Date, numberOfWeeks: number): Date {
  const newDate = new Date(date)
  newDate.setUTCDate(date.getUTCDate() + 7 * numberOfWeeks)
  return newDate
}

export function addMonths(date: Date, numberOfMonths: number): Date {
  const newDate = new Date(date)
  newDate.setUTCMonth(date.getUTCMonth() + numberOfMonths)
  return newDate
}

export function subDays(date: Date, numberOfDays: number): Date {
  const newDate = new Date(date)
  newDate.setUTCDate(date.getUTCDate() - numberOfDays)
  return newDate
}

export function subMonths(date: Date, numberOfMonths: number): Date {
  const newDate = new Date(date)
  newDate.setUTCMonth(date.getUTCMonth() - numberOfMonths)
  return newDate
}

export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getUTCFullYear() === date2.getUTCFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth() &&
    date1.getUTCDate() === date2.getUTCDate()
  )
}

export function differenceInMonths(date1: Date, date2: Date): number {
  const startDate = new Date(Math.min(date1.getTime(), date2.getTime()))
  const endDate = new Date(Math.max(date1.getTime(), date2.getTime()))

  let months =
    (endDate.getUTCFullYear() - startDate.getUTCFullYear()) * 12 +
    (endDate.getUTCMonth() - startDate.getUTCMonth())

  // Adjust if the end day is before the start day in the month
  if (endDate.getUTCDate() < startDate.getUTCDate()) {
    months--
  }

  return date1.getTime() > date2.getTime() ? -months : months
}

export function startOfWeek(date: Date): Date {
  const day = date.getUTCDay() // Sunday = 0, Monday = 1, ..., Saturday = 6
  const diffToMonday = day === 0 ? -6 : 1 - day // Adjust so Monday is 0

  const result = new Date(date) // Copy original date
  result.setUTCDate(date.getUTCDate() + diffToMonday) // Shift to Monday
  result.setUTCHours(0, 0, 0, 0) // Set time to 00:00:00.000

  return result
}

export function startOfMonth(date: Date): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0, 0),
  )
}

export function getWeekNumber(date: Date): number {
  const target = new Date(date)
  const dayNumber = (date.getUTCDay() + 6) % 7 // Monday=0, Sunday=6

  // Set target to Thursday of this week
  target.setUTCDate(target.getUTCDate() - dayNumber + 3)

  // First Thursday of the year
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4))

  // Calculate full weeks to the target date
  const weekNumber =
    1 + Math.round((target.getTime() - firstThursday.getTime()) / weekInMs)

  // Handle week 0 as week 52/53 of previous year
  if (weekNumber < 1) {
    // Get the last week number of the previous year
    return getWeekNumber(
      new Date(Date.UTC(target.getUTCFullYear() - 1, 11, 31)),
    )
  }

  return weekNumber
}

export function max(dates: Date[]): Date | undefined {
  return dates.length === 0
    ? undefined
    : dates.reduce((latest, current) => (current > latest ? current : latest))
}

export interface DateRange {
  startDate: Date
  endDate: Date
}

type PrettyPrintDateRangeOptions = Pick<
  PrettyPrintDateRangeDateOptions,
  "locale"
>

export function prettyPrintDateRange(
  dateRange: DateRange,
  options: PrettyPrintDateRangeOptions = {},
): string {
  const { startDate, endDate } = dateRange
  const { locale = "en-US" } = options
  return `${prettyPrintDateRangeDate(startDate, { locale })} - ${prettyPrintDateRangeDate(endDate, { locale })}`
}

type PrettyPrintDateRangeDateOptions = Pick<
  PrettyPrintDateOptions,
  "locale" | "year"
>

export function prettyPrintDateRangeDate(
  date: Date,
  options: PrettyPrintDateRangeDateOptions = {},
): string {
  const { locale = "en-US", ...otherOptions } = options
  return prettyPrintDate(date, { ...otherOptions, locale, time: false })
}

export function isSameDateRangeByDay(
  dateRange1: DateRange,
  dateRange2: DateRange,
): boolean {
  return (
    isSameDay(dateRange1.startDate, dateRange2.startDate) &&
    isSameDay(dateRange1.endDate, dateRange2.endDate)
  )
}

/**
 * Returns relative time string that updates every minute/hour/day.
 */
export function useRealTimeDateDiff(date: Date) {
  const [dateDiff, setDateDiff] = useState(getDateDiffString(date))

  useEffect(() => {
    let timeout: number

    function tick() {
      setDateDiff(getDateDiffString(date))

      const timeDiffMs = Math.max(0, Date.now() - date.getTime())

      const frequency = getUpdateFrequency(timeDiffMs)

      // Unnoticeable time buffer to be sure we're after the new minute/hour/day
      const epsilonMs = secondInMs

      // Imagine update happened at 1 second and you're on 1 minute 59 seconds,
      // we want to update the relative time in 3 seconds (60 - ((119 - 1) % 60) + 1)
      const nextUpdateInMs = frequency - (timeDiffMs % frequency) + epsilonMs

      timeout = window.setTimeout(tick, nextUpdateInMs)
    }

    tick()

    return () => clearTimeout(timeout)
  }, [date])

  return dateDiff
}

const getUpdateFrequency = (timeDiffMs: number) => {
  if (timeDiffMs < hourInMs) {
    return minuteInMs
  }

  if (timeDiffMs < dayInMs) {
    return hourInMs
  }

  return dayInMs
}
